import {
  OnboardingOrganizationSchema,
  OrganizationInvoiceTemplateSchema,
  OrganizationSchema,
  UpdateOrganizationTemplateSchema,
  ZodCreateOrganizationSchema,
  ZodUpdateOrganizationSchema,
} from "@billingnow/zod-schema";
import { apiClient } from "./client";

export type Organization = OrganizationSchema;
export type OrganizationInvoiceTemplate = OrganizationInvoiceTemplateSchema;
export type UpdateOrganizationTemplateData = UpdateOrganizationTemplateSchema;

export const getOrganization = async (): Promise<Organization | null> => {
  const response = await apiClient.get<{ organization: Organization }>("/api/organizations/active");
  return response.data.organization;
};

export const getAllOrganizations = async (): Promise<Organization[]> => {
  const response = await apiClient.get<{ organizations: Organization[] }>("/api/organizations");
  return response.data.organizations;
};

export const getActiveOrganization = async (): Promise<Organization | null> => {
  const response = await apiClient.get<{ organization: Organization | null }>("/api/organizations/active");
  return response.data.organization;
};

export const switchActiveOrganization = async (
  organizationId: string,
): Promise<{ success: boolean; activeOrganizationId: string }> => {
  const response = await apiClient.post<{ success: boolean; activeOrganizationId: string }>(
    `/api/organizations/switch/${organizationId}`,
  );
  return response.data;
};

export const createOnboardingOrganization = async (
  organizationData: OnboardingOrganizationSchema,
): Promise<Organization> => {
  const response = await apiClient.post<Organization>("/api/organizations/onboarding", organizationData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data;
};

export const createOrganization = async (organizationData: ZodCreateOrganizationSchema): Promise<Organization> => {
  const response = await apiClient.post<{ organization: Organization }>("/api/organizations", organizationData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data.organization;
};

export const updateOrganization = async (organizationData: ZodUpdateOrganizationSchema): Promise<Organization> => {
  const response = await apiClient.put<{ organization: Organization }>(
    `/api/organizations/${organizationData.id}`,
    organizationData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data.organization;
};

export const deleteOrganization = async (
  organizationId: string,
): Promise<{ message: string; organization: Organization }> => {
  const response = await apiClient.delete<{ message: string; organization: Organization }>(
    `/api/organizations/${organizationId}`,
  );
  return response.data;
};

export const getOrganizationTemplate = async (): Promise<OrganizationInvoiceTemplate | null> => {
  const response = await apiClient.get<{ template: OrganizationInvoiceTemplate }>(`/api/organizations/template`);
  return response.data.template;
};

export const updateOrganizationTemplate = async (
  templateData: UpdateOrganizationTemplateData,
): Promise<OrganizationInvoiceTemplate> => {
  const response = await apiClient.put<{ template: OrganizationInvoiceTemplate }>(
    `/api/organizations/template`,
    templateData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data.template;
};

export default getOrganization;
